import clsx from "clsx";
import { Link } from "react-router-dom";
import { FileSignature } from "lucide-react";
import type { Contract, ContractStatus } from "@/api/contracts";
import { ENDED, KIND_LABEL, PROGRESS, STATUS, lastUpdate, statusColor } from "@/lib/contracts";
import { eur, fmtDayShort } from "@/lib/format";

/** The road to signature as six segments, filled up to where the contract is. */
export function ContractTrack({
  status,
  compact = false,
}: {
  status: ContractStatus;
  compact?: boolean;
}) {
  const ended = ENDED.includes(status);
  const at = ended ? PROGRESS.length - 1 : PROGRESS.indexOf(status);
  return (
    <div className="flex items-center gap-1" aria-label={`Status: ${STATUS[status].label}`}>
      {PROGRESS.map((s, i) => (
        <span
          key={s}
          title={STATUS[s].label}
          className={clsx("flex-1 rounded-full", compact ? "h-1.5" : "h-2")}
          style={{
            background: ended
              ? "rgb(var(--mut-500) / 0.25)"
              : i <= at
                ? statusColor(s, i === at ? 1 : 0.45)
                : "rgb(var(--fg) / 0.07)",
          }}
        />
      ))}
    </div>
  );
}

export function StatusPill({ status }: { status: ContractStatus }) {
  return (
    <span
      className="chip whitespace-nowrap"
      style={{ background: statusColor(status, 0.12), color: statusColor(status) }}
    >
      {STATUS[status].label}
    </span>
  );
}

export function WaitingPill({ contract: c }: { contract: Contract }) {
  if (!c.waiting_on) return null;
  const us = c.waiting_on === "us";
  return (
    <span
      className={clsx(
        "chip whitespace-nowrap",
        us ? "bg-accent-amber/12 text-accent-amber" : "bg-fg/[0.05] text-mut-300",
      )}
    >
      {us ? "Your move" : "Waiting on them"}
    </span>
  );
}

/** One contract in a list: what it is, how far along, whose move, what happened last. */
export function ContractRow({
  contract: c,
  showAccount = true,
}: {
  contract: Contract;
  showAccount?: boolean;
}) {
  const update = lastUpdate(c);
  return (
    <li>
      <Link
        to={`/contracts/${c.id}`}
        className="grid grid-cols-1 gap-2.5 px-4 py-3.5 transition-colors hover:bg-fg/[0.03] md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)] md:items-center md:gap-6"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
            style={{ background: statusColor(c.status, 0.12), color: statusColor(c.status) }}
          >
            <FileSignature className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-md font-medium">{c.title}</span>
            <span className="block truncate text-sm text-mut-500 tabular-nums">
              {KIND_LABEL[c.kind]}
              {showAccount && c.account_name ? ` · ${c.account_name}` : ""}
              {c.value_eur != null ? ` · ${eur(c.value_eur, { short: true })}` : ""}
            </span>
          </span>
        </span>

        <span className="block min-w-0">
          <span className="mb-1.5 flex items-center gap-2 text-2xs font-semibold">
            <span style={{ color: statusColor(c.status) }}>{STATUS[c.status].label}</span>
            {c.status === "signed" && c.end_date && (
              <span className="font-normal text-mut-500">until {fmtDayShort(c.end_date)}</span>
            )}
          </span>
          <ContractTrack status={c.status} compact />
        </span>

        <span className="flex min-w-0 items-center gap-2 text-sm">
          <WaitingPill contract={c} />
          <span className="min-w-0 flex-1 truncate text-mut-500">{update}</span>
          <span className="shrink-0 text-xs text-mut-500">{fmtDayShort(c.last_event_at)}</span>
        </span>
      </Link>
    </li>
  );
}
